/**
 * The generic floor object — the ONLY thing the engine renders.
 *
 * The engine is domain-blind: `type` is a RendererRegistry key (custom types
 * are registrable at runtime) and `metadata` is an opaque bag the engine
 * never inspects. POS meaning (unitId, occupancy status, capacity) is
 * injected by the container layer through `metadata` and consumed only by
 * the renderer registered for that type.
 *
 * @module floorplan/models/object
 * @category FloorPlan
 */

import type { Vec2 } from './geometry';

/**
 * Generic visual styling. Every field is optional — renderers fall back to
 * theme defaults resolved at record time (see `useFloorTheme`).
 */
export type ObjectStyle = {
  /** Fill color (hex / rgba string). */
  fill?: string;
  /** Stroke color. */
  stroke?: string;
  /** Stroke width in world units. */
  strokeWidth?: number;
  /** Corner radius in world units (rect-family renderers). */
  cornerRadius?: number;
  /** 0–1 opacity applied to the whole object group. */
  opacity?: number;
  /** Primary label text (table number, room name, …). */
  text?: string;
  /**
   * Secondary label drawn under the primary one (smaller). Generic — any
   * domain can use it; the POS binding sets it to the occupied table's
   * order number so the floor mirrors the grid's `#123` line.
   */
  subLabel?: string;
  /** Label color. */
  textColor?: string;
  /** Label font size in world units. */
  fontSize?: number;
  /**
   * Status-badge dot color (top-right corner). Generic — any domain can use
   * it; the POS binding sets it to the unit-status color so status reads as
   * a badge, not just a tint.
   */
  badge?: string;
  /**
   * Seat-stub count drawn around rect-family objects (split top/bottom).
   * Consumed by the built-in table renderer; other renderers ignore it.
   */
  seats?: number;
};

/**
 * A single object on the floor plan.
 *
 * `width`/`height` are the unscaled base dimensions; `scale` is a uniform
 * multiplier (effective size = `width * scale`). `rotation` is degrees,
 * clockwise, around the object's center.
 */
export type FloorObject = {
  id: string;
  /** RendererRegistry key — `'rect' | 'circle' | 'table' | …` or custom. */
  type: string;
  /** World x of the top-left corner (unrotated). */
  x: number;
  /** World y of the top-left corner (unrotated). */
  y: number;
  width: number;
  height: number;
  /** Degrees, clockwise, around the object center. */
  rotation: number;
  /** Uniform scale multiplier applied around the object center. */
  scale: number;
  visible: boolean;
  locked: boolean;
  /** Draw order within its layer (higher = on top). */
  zIndex: number;
  /** Owning layer id — see `Layer`. */
  layerId: string;
  style?: ObjectStyle;
  /** Vertices for polygon / line types, in world coords relative to (x, y). */
  points?: Vec2[];
  /**
   * Grouping membership (ENGINE-owned — deliberately NOT in `metadata`,
   * which stays opaque to the engine). The value is the id of the owning
   * `group`-type node; tapping any member selects the whole group.
   */
  groupId?: string;
  /** OPAQUE to the engine. The POS binding writes `{ unitId, status, … }`. */
  metadata: Record<string, unknown>;
};

/**
 * Built-in renderer keys registered at engine startup. Semantic aliases
 * (table / chair / wall / …) map onto base geometry renderers with default
 * styling so business surfaces read naturally — but the engine treats every
 * key identically through the registry (no switch statements).
 */
export const FLOOR_OBJECT_TYPE = {
  RECT: 'rect',
  CIRCLE: 'circle',
  POLYGON: 'polygon',
  LINE: 'line',
  TEXT: 'text',
  IMAGE: 'image',
  ICON: 'icon',
  // Semantic aliases (base renderer + default style/metadata).
  TABLE: 'table',
  CHAIR: 'chair',
  WALL: 'wall',
  COUNTER: 'counter',
  ROOM: 'room',
  DOOR: 'door',
  WINDOW: 'window',
  SHAPE: 'shape',
  /** Logical grouping node — owns child ids in `metadata.childIds`. */
  GROUP: 'group',
} as const;

export type FloorObjectType =
  (typeof FLOOR_OBJECT_TYPE)[keyof typeof FLOOR_OBJECT_TYPE];
